// src/logic/synonym-expander.ts
// Runtime-only lexical expansion for matcher consumption.
// - Keeps visible triggers unchanged (Tag/Preview unaffected).
// - No persistence changes.
// - Strictly lexical; zero matcher/audio/UI edits.

import type { CueNote } from "../types";
import { synonyms as TRIGGER_SYNONYMS } from "./synonyms"; // path confirmed

// ---- Normalizer (aligns with matcher base) ---------------------------------
const PUNCT_RE = /[^\p{L}\p{N}\s']/gu;
const MULTISPACE_RE = /\s+/g;

function normalizeBase(s: string): string {
  let t = (s || "").toLowerCase().normalize("NFKC");

  // CS lexical shim (word-boundary safe)
  t = t.replace(/\bcs\b/g, "customer success");
  t = t.replace(/\bcsm\b/g, "customer success manager");
  t = t.replace(/\bclient\b/g, "customer");
  // NOTE: intentionally NOT aliasing "teams" -> "stakeholders" globally (avoids bleed)

  // tiny "why us" hardening (matcher parity)
  t = t.replace(/\bwhy\s*s\b/g, "why us");
  t = t.replace(/\bwhy\s+ys\b/g, "why us");

  t = t.replace(PUNCT_RE, " ");
  t = t.replace(MULTISPACE_RE, " ").trim();
  return t;
}

function tokenize(s: string): string[] {
  return normalizeBase(s).split(" ").filter(Boolean);
}

// ---- Light morphology (tail-only, conservative) ----------------------------
function morphVariants(token: string): string[] {
  const out = new Set<string>([token]);
  if (token.endsWith("s") && token.length > 3) out.add(token.slice(0, -1)); else out.add(token + "s");
  if (!token.endsWith("ed")) out.add(token + "ed");
  if (!token.endsWith("ing")) out.add(token + "ing");
  if (token === "lead") out.add("led");
  if (token === "manage") { out.add("managed"); out.add("managing"); out.add("management"); }
  if (token === "project") out.add("projects");
  return Array.from(out);
}

function phraseMorphs(phrase: string): string[] {
  const toks = tokenize(phrase);
  if (!toks.length) return [];
  const head = toks.slice(0, -1), tail = toks[toks.length - 1];
  return morphVariants(tail).map(t => [...head, t].join(" "));
}

// ---- Board-title synonyms (human-readable keys) ----------------------------
const RAW_BOARD_SYNONYMS: Record<string, string[]> = {
  "Elevator Pitch": [
    "quick career summary",
    "quick introduction",
    "give me an overview",
    "walk me through your background",
    // make EP show up when user literally says it
    "your elevator pitch",
    "what is your elevator pitch",
    "elevator pitch about yourself",
    // resume phrasing
    "walk me through your resume"
  ],

  "Why This Role": [
    "what about this opportunity excites you",
    "what specifically about this opportunity excites you today",
    "why are you excited about this role",
    "why do you want this job",
    "why here",
    // company-focused phrasing
    "why this company",
    "what about this company",
    "what was it about this company that convinced you to apply",
    "why apply here",
    "hat in the ring"
  ],

  "STAR — Roadblock": [
    "went sideways",
    "walk me through a time things went sideways",
    "how you responded",
    "jammed up",
    "blocked",
    "got stuck",
    // roadblock-y variants
    "blew up on a project",
    "blew up",
    "went off the rails",
    "fire drill"
    // (moved stakeholder-alignment variants to Project Management)
  ],

  "STAR — Leadership": [
    "share a time you stepped up to lead and what changed",
    "stepped up to lead",
    "share a time you stepped up to lead",
    "took charge",
    "led the group",
    "what changed",
    "led a team",
    "took ownership"
  ],

  "Career Goals": [
    "how do you picture your career evolving",
    "where are you headed",
    "long term goals",
    "career trajectory"
  ],

  "Project Management": [
    "keep everyone in sync",
    "when multiple teams are involved how do you keep everyone in sync",
    "coordinate across teams",
    "keep teams aligned",
    // stakeholder-alignment variants (substring-friendly)
    "keep stakeholders aligned",
    "keeping stakeholders aligned",
    "keep stakeholders in sync",
    "stakeholder alignment",
    "align stakeholders"
  ],

  "Metrics & Success": [
    "what numbers matter most",
    "what numbers matter most when you judge impact",
    "key metrics you track",
    "kpis you watch",
    "what does success look like",
    "what does success look like to you",
    "what does success look like in this role"
  ],

  "Salary & Compensation": [
    "base bonus equity",
    "how are you thinking about base bonus and equity",
    "total comp",
    "compensation range"
  ],
};

// Build a normalized lookup once so titles with punctuation (&, —) match.
const NORM_BOARD_SYNONYMS: Record<string, string[]> = (() => {
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(RAW_BOARD_SYNONYMS)) {
    out[normalizeBase(k)] = v.slice();
  }
  return out;
})();

// ---- Adj-Drop Backoff (v62) -----------------------------------------------
const NOUN_ALLOW = new Set(["teams", "stakeholders", "metrics", "roadmap", "budget"]);

function isVerbish(token: string): boolean {
  return /^[a-z]+$/.test(token) && token.length >= 3;
}

function verbLightForms(observed: string): string[] {
  const forms = new Set<string>();
  const add = (s: string) => { if (s && s.length >= 3) forms.add(s); };
  const t = observed; add(t);
  if (t.endsWith("ing") && t.length > 4) {
    const base = t.slice(0, -3); add(base); add(base + "ed");
  } else if (t.endsWith("ed") && t.length > 4) {
    const base = t.slice(0, -2); add(base); add(base + "ing");
  } else {
    const base = t; add(base + "ed"); add(base + "ing");
  }
  return Array.from(forms);
}

function adjDropAliasesForTrigger(trigger: string): string[] {
  const toks = tokenize(trigger);
  if (toks.length < 3) return [];
  let nounIdx = -1;
  for (let i = toks.length - 1; i >= 0; i--) {
    if (NOUN_ALLOW.has(toks[i])) { nounIdx = i; break; }
  }
  if (nounIdx <= 0) return [];
  const verb = toks[0];
  if (!isVerbish(verb)) return [];
  if (nounIdx - 1 < 1) return []; // require at least one adj token between verb and noun
  const noun = toks[nounIdx];
  const out = new Set<string>();
  for (const vf of verbLightForms(verb)) out.add(`${vf} ${noun}`.trim());
  return Array.from(out);
}

function expandWithAdjDrop(triggers: string[]): string[] {
  const ephemerals: string[] = [];
  for (const t of triggers) {
    for (const alias of adjDropAliasesForTrigger(t)) ephemerals.push(alias);
  }
  return ephemerals;
}

// ---- Synthesis helpers -----------------------------------------------------
function synthFromSynonyms(phrases: string[]): string[] {
  const out = new Set<string>();
  for (const s of phrases) {
    const norm = normalizeBase(s);
    if (!norm) continue;
    out.add(norm);
    for (const m of phraseMorphs(norm)) out.add(m);
  }
  return Array.from(out);
}

// ---- Public API ------------------------------------------------------------
export function expandNotesForMatcher(notes: CueNote[]): CueNote[] {
  const expanded: CueNote[] = [];

  for (const note of notes || []) {
    const seen = new Set<string>();
    const keep: string[] = [];

    // 1) Keep originals (first-seen casing)
    for (const tr of Array.isArray(note.triggers) ? note.triggers : []) {
      const raw = (tr ?? "").trim();
      if (!raw) continue;
      const k = normalizeBase(raw);
      if (seen.has(k)) continue;
      seen.add(k);
      keep.push(raw);
    }

    const extras: string[] = [];

    // 2) Trigger-synonym expansions (keys are canonical trigger phrases)
    for (const tr of keep) {
      const key = normalizeBase(tr);
      const syns = TRIGGER_SYNONYMS[key] || [];
      const add = synthFromSynonyms(syns);
      for (const a of add) if (!seen.has(a)) { seen.add(a); extras.push(a); }
      // tail morphs of the original
      for (const m of phraseMorphs(tr)) {
        const mn = normalizeBase(m);
        if (!seen.has(mn)) { seen.add(mn); extras.push(m); }
      }
    }

    // 2.5) Adj-Drop Backoff (runtime-only, note-local)
    {
      const aliases = expandWithAdjDrop(keep);
      for (const a of aliases) {
        const an = normalizeBase(a);
        if (!seen.has(an)) { seen.add(an); extras.push(an); }
      }
    }

    // 3) Board-title expansions (keys are normalized note.title)
    const titleKey = normalizeBase(note.title || "");
    const boardSyns = NORM_BOARD_SYNONYMS[titleKey];
    if (boardSyns && boardSyns.length) {
      const add = synthFromSynonyms(boardSyns);
      for (const a of add) if (!seen.has(a)) { seen.add(a); extras.push(a); }
    }

    expanded.push({ ...note, triggers: keep.concat(extras) });
  }

  return expanded;
}
